/** Pure helpers for editing a GoalEnvelope in the UI. No scoring: the backend computes every number. */
import type { FacetKey, GoalEnvelope } from '@/lib/api';

export const FACET_KEYS: readonly FacetKey[] = ['speed', 'consistency', 'interaction', 'closing'];

export type ScalarGoalKey = 'budget_per_card_usd' | 'salt_tolerance' | 'operational_threshold' | 'deck_budget_usd';

export const emptyGoals = (): GoalEnvelope => ({
  facet_targets: {}, budget_per_card_usd: null, salt_tolerance: null, operational_threshold: null, deck_budget_usd: null,
});

/** A full-shape copy (so two envelopes compare by value); null = no stored goals = empty. */
export function normalizeGoals(g: GoalEnvelope | null | undefined): GoalEnvelope {
  const base = emptyGoals();
  if (!g) return base;
  return {
    facet_targets: { ...g.facet_targets },
    budget_per_card_usd: g.budget_per_card_usd ?? null,
    salt_tolerance: g.salt_tolerance ?? null,
    operational_threshold: g.operational_threshold ?? null,
    deck_budget_usd: g.deck_budget_usd ?? null,
  };
}

/** Value equality, insensitive to facet_targets key order. */
export function sameGoals(a: GoalEnvelope, b: GoalEnvelope): boolean {
  const x = normalizeGoals(a);
  const y = normalizeGoals(b);
  const xk = Object.keys(x.facet_targets);
  const yk = Object.keys(y.facet_targets);
  if (xk.length !== yk.length) return false;
  const targets = x.facet_targets as Record<string, number | undefined>;
  const others = y.facet_targets as Record<string, number | undefined>;
  if (!xk.every((k) => k in others && targets[k] === others[k])) return false;
  return x.budget_per_card_usd === y.budget_per_card_usd
    && x.salt_tolerance === y.salt_tolerance
    && x.operational_threshold === y.operational_threshold
    && x.deck_budget_usd === y.deck_budget_usd;
}

export const isEmptyGoals = (g: GoalEnvelope | null | undefined): boolean => sameGoals(normalizeGoals(g), emptyGoals());

/** '' or anything non-finite -> null (unset); otherwise the number, which is NOT range-limited. */
export function parseGoalNumber(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function withFacetTarget(g: GoalEnvelope, facet: FacetKey, raw: string): GoalEnvelope {
  const next = normalizeGoals(g);
  const v = parseGoalNumber(raw);
  if (v === null) delete next.facet_targets[facet];
  else next.facet_targets[facet] = v;
  return next;
}

export function withScalar(g: GoalEnvelope, key: ScalarGoalKey, raw: string): GoalEnvelope {
  return { ...normalizeGoals(g), [key]: parseGoalNumber(raw) };
}
